import { buildCostReportAnalyzePrompt } from 'features/cost-report/utils/buildCostReportAnalyzePrompt';
import { useCostReportTimeRangeSelection } from 'features/cost-report/hooks/useCostReportTimeRangeSelection';

import { ChartMoney } from './_components/ChartMoney';
import { ChartTitle } from './_components/ChartTitle';
import { makeChartTooltip } from './_components/ChartTooltip';
import { CostPerProvisionedResourceChart } from './_components/CostPerProvisionedResourceChart';
import {
  DataPointMapper,
  useNormalizedResourceCostChartData,
} from './_hooks/useNormalizedResourceCostChartData';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const chartDataMapper: DataPointMapper = ({
  normalizedCostPerGib,
  onDemandCostPerGib,
  spotCostPerGib,
  fallbackCostPerGib,
}) => ({
  normalizedCost: normalizedCostPerGib,
  onDemandCost: onDemandCostPerGib,
  spotCost: spotCostPerGib,
  fallbackCost: fallbackCostPerGib,
});

type Props = { costType: string };

export const MemoryCostChart = ({ costType }: Props) => {
  const { cluster } = useCluster();
  const { getRange } = useCostReportTimeRangeSelection();
  const { isLoading, data } =
    useNormalizedResourceCostChartData(chartDataMapper);
  const { tooltipHeader, tooltipFooter } = makeChartTooltip({ unit: 'GiB' });

  return (
    <CostPerProvisionedResourceChart
      header={
        <ChartTitle
          title={`${costType} MEM COST`}
          infoTooltip="Total daily memory spend divided by the provisioned memory"
          cost={
            <ChartMoney
              mapper={({ hourlyCostPerRamGib }) => hourlyCostPerRamGib}
            />
          }
        />
      }
      tooltipHeader={tooltipHeader}
      tooltipFooter={tooltipFooter}
      data={data}
      loading={isLoading}
      testId="cost-per-provisioned-resource-chart-memory"
      buildPrompt={buildCostReportAnalyzePrompt({
        scopeName: cluster?.name,
        context: 'memory cost per provisioned resource',
        getDefaultRange: getRange,
      })}
    />
  );
};
